import React, {FC, useState} from 'react'
import {useQuery} from 'react-query'
import {generateInsightByType, checkRushMindAvailability} from '../core/_requests'
import RushmindInsightText from './RushmindInsightText'
import {useAuth} from '../../../../modules/auth'
import {Link} from 'react-router-dom'
import {useIsChecklistVisible} from '../../../../modules/onboarding/hooks/useOnboardingProgress'

// CSS para efeitos visuais
const typewriterStyles = `
  .blink-icon {
    animation: blink 2s infinite;
  }

  @keyframes blink {
    0%, 50% { 
      opacity: 1; 
      transform: scale(1);
    }
    51%, 100% { 
      opacity: 0.4; 
      transform: scale(0.95);
    }
  }

  /* Destacar números e percentuais em negrito */
  .insight-text {
    font-weight: 400;
  }

  /* Destaque numérico e links do RushMind: tom da marca, definido em style.css (.rushmind-link) */
  .insight-text .number-highlight {
    font-weight: 700;
  }

  /* Cursor da máquina de escrever */
  @keyframes blink {
    0%, 50% { 
      opacity: 1; 
    }
    51%, 100% { 
      opacity: 0; 
    }
  }
`

const ALLOWED_INSIGHT_TYPES = new Set([
  'general',
  'clients',
  'tasks',
  'margin',
  'time_tracking',
  'planning',
  'productivity',
  'profitability',
  'rushmind_personal',
  'rushmind_pm',
  'rushmind_auto',
])

interface AIInsight {
  insight: string
  type: string
  type_label: string
  generated_at: string
  insight_date: string
  is_onboarding?: boolean
  user_id?: number
}

interface RushMindAvailability {
  success: boolean
  should_show_rushmind: boolean
  has_rushmind_data?: boolean
  has_rushmind_module?: boolean
  has_sufficient_data_for_ai_insights?: boolean
  insights_by_type_requires_upgrade?: boolean
  reason?: string
}

const AI_INSIGHT_BASE_TYPES = ['general', 'clients', 'margin', 'planning', 'productivity', 'profitability']

const AIInsightsGrid: FC = () => {
  const {currentUser} = useAuth()
  // O checklist de primeiros passos já orienta o usuário novo: o insight de onboarding do RushMind
  // não aparece junto (duas vozes dizendo a mesma coisa) e volta quando o checklist é ocultado.
  const checklistVisible = useIsChecklistVisible()

  // Estado para o tipo de insight selecionado aleatoriamente. Inicializado de forma preguiçosa
  // (calculado uma única vez, na montagem) em vez de sempre começar em 'general' e só depois
  // corrigir num useEffect — a versão antiga disparava uma busca por 'general' logo de cara
  // (chamada à IA desperdiçada) e só depois trocava pro tipo sorteado de verdade, causando um
  // flash visual. Também evita o card ficar "preso" no mesmo tipo enquanto o componente
  // permanecer montado (ex: navegação interna do SPA sem reload completo).
  const [selectedInsightType] = useState<string>(() => {
    const pool = currentUser?.id ? [...AI_INSIGHT_BASE_TYPES, 'rushmind_personal'] : AI_INSIGHT_BASE_TYPES
    return pool[Math.floor(Math.random() * pool.length)] ?? 'general'
  })

  // Verificar se o RushMind deve ser exibido (OpenAI disponível + dados suficientes)
  const {
    data: availabilityData,
    isLoading: availabilityLoading,
    error: availabilityError
  } = useQuery<RushMindAvailability>(['rushmind-availability'], checkRushMindAvailability, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30, // 30 minutos
  })

  // Função para destacar números no texto
  const highlightNumbers = (text: string) => {
    // Regex para capturar números, percentuais, horas, e valores monetários
    const numberRegex = /(\d+(?:[.,]\d+)?(?:%|h|R\$)?|\d+(?:[.,]\d+)?\s*(?:dias?|semanas?|meses?|anos?|horas?|minutos?|projetos?|tarefas?|usuários?|clientes?))/gi
    
    return text.replace(numberRegex, '<span class="number-highlight">$1</span>')
  }

  // Buscar insight para o tipo selecionado aleatoriamente
  const {
    data: insightData,
    isLoading: insightLoading,
    error: insightError
  } = useQuery(['ai-insight', selectedInsightType], () => generateInsightByType(selectedInsightType), {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hora
    enabled:
      availabilityData?.should_show_rushmind === true &&
      availabilityData?.insights_by_type_requires_upgrade !== true,
  })

  // Determina se deve mostrar o RushMind ou o placeholder
  const shouldShowRushMind = availabilityData?.should_show_rushmind === true
  const insightsByTypeRequiresUpgrade = availabilityData?.insights_by_type_requires_upgrade === true
  const shouldShowUpgradeCta =
    (availabilityData?.reason === 'plan_upgrade_required' &&
      availabilityData?.has_rushmind_data === true &&
      availabilityData?.has_rushmind_module === false) ||
    insightsByTypeRequiresUpgrade
  const showPlaceholder = !availabilityLoading && !availabilityError && !shouldShowRushMind

  // Se está carregando a disponibilidade, não exibe nada
  if (availabilityLoading) {
    return null
  }

  // Se há erro na verificação de disponibilidade, não exibe nada
  if (availabilityError) {
    return null
  }

  // Se deve mostrar placeholder (instância sem dados suficientes)
  if (showPlaceholder) {
    return (
      <>
        <style>{typewriterStyles}</style>
        <div className='my-2'>
          {shouldShowUpgradeCta ? renderUpgradeCtaCard(!!currentUser?.administrator) : renderFallbackCard()}
        </div>
      </>
    )
  }

  // Instância em onboarding para o widget mas com dados suficientes para IA sem módulo Growth — só CTA de upgrade
  if (shouldShowRushMind && shouldShowUpgradeCta) {
    return (
      <>
        <style>{typewriterStyles}</style>
        <div className='my-2'>
          <div className='mb-3'>
            <h5 className='text-dark fw-bold mb-0'>
              <i className='bi bi-magic text-brand me-2 blink-icon'></i>
              RushMind
            </h5>
          </div>
          {renderUpgradeCtaCard(!!currentUser?.administrator)}
        </div>
      </>
    )
  }

  // Se deve mostrar o RushMind normal (instância com dados)
  if (shouldShowRushMind) {
    return (
      <>
        <style>{typewriterStyles}</style>
        <div className='my-2'>
          <div className='mb-3'>
            <h5 className='text-dark fw-bold mb-0'>
              <i className='bi bi-magic text-brand me-2 blink-icon'></i>
              RushMind
            </h5>
          </div>
          
          {renderInsightCard(
            selectedInsightType,
            insightData,
            insightLoading,
            insightError,
            currentUser?.id,
            checklistVisible
          )}
        </div>
      </>
    )
  }

  // Se não deve mostrar nada (caso de fallback)
  return null
}

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'profitability':
        return 'bi-graph-up-arrow text-success'
      case 'productivity':
        return 'bi-speedometer2 text-brand'
      case 'clients':
        return 'bi-people text-info'
      case 'tasks':
        return 'bi-list-task text-purple'
      case 'planning':
        return 'bi-calendar-check text-orange'
      case 'margin':
        return 'bi-pie-chart text-danger'
      case 'time_tracking':
        return 'bi-clock text-teal'
      case 'general':
      default:
        return 'bi-lightbulb text-warning'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'profitability':
        return 'border-success border-start border-4'
      case 'productivity':
        return 'border-brand border-start border-4'
      case 'clients':
        return 'border-info border-start border-4'
      case 'tasks':
        return 'border-purple border-start border-4'
      case 'planning':
        return 'border-orange border-start border-4'
      case 'margin':
        return 'border-danger border-start border-4'
      case 'time_tracking':
        return 'border-teal border-start border-4'
      case 'general':
      default:
        return 'border-warning border-start border-4'
      }
    }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'profitability':
        return 'Lucratividade'
      case 'productivity':
        return 'Produtividade'
      case 'clients':
        return 'Clientes'
      case 'tasks':
        return 'Tarefas'
      case 'planning':
        return 'Planejamento'
      case 'margin':
        return 'Margem'
      case 'time_tracking':
        return 'Apontamento'
      case 'general':
      default:
        return 'Geral'
      }
    }

  const renderInsightCard = (
    type: string,
    data: any,
    isLoading: boolean,
    error: any,
    currentUserId?: number,
    hideOnboardingTip = false
  ) => {
    if (isLoading) {
      return (
        <div className='card card-custom bg-body'>
          <div className='card-body p-3'>
            <div className='mb-0 d-flex align-items-center justify-content-center' style={{ minHeight: '48px' }}>
              <div className='d-flex align-items-center text-muted'>
                <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                <span className='fs-6'>Gerando insight personalizado...</span>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (error || !data?.success || !data?.data) {
      return renderFallbackCard()
    }

    const insight: AIInsight = data.data

    // Verificação adicional de segurança
    if (!insight || !insight.insight) {
      return renderFallbackCard()
    }

    if (!ALLOWED_INSIGHT_TYPES.has(insight.type)) {
      return renderFallbackCard()
    }

    if (insight.type === 'rushmind_personal' && currentUserId && insight.user_id && insight.user_id !== currentUserId) {
      return renderFallbackCard()
    }

    // Verificar se o texto está completamente processado
    // O texto está malformado se contém padrões de HTML/JSX não processado
    const hasMalformedHTML = insight.insight && (
      insight.insight.includes('class="rushmind-link">') || // Tag de fechamento sem abertura
      insight.insight.includes('?taskId=') || // Query string exposta
      insight.insight.includes('</a>') && !insight.insight.includes('<a') || // Tag de fechamento sem abertura
      (insight.insight.match(/<[^>]*>/g) || []).some(tag => 
        !tag.includes('</') && !tag.endsWith('>') // Tags HTML malformadas
      )
    )

    const isTextReady = insight.insight && 
      insight.insight.trim().length > 0 &&
      !hasMalformedHTML

    // Se o texto não estiver pronto, mostrar loading
    if (!isTextReady) {
      return (
        <div className='card card-custom bg-body'>
          <div className='card-body p-3'>
            <div className='mb-0 d-flex align-items-center justify-content-center' style={{ minHeight: '48px' }}>
              <div className='d-flex align-items-center text-muted'>
                <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                <span className='fs-6'>Processando mensagem...</span>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (insight.is_onboarding && hideOnboardingTip) return null

    return (
      <div className={`card card-custom ${insight.is_onboarding ? 'rushmind-onboarding-tip' : 'bg-body'}`}>
        <div className='card-body p-3'>
          <div className='mb-0' style={{ minHeight: '48px' }}>
            {insight.is_onboarding ? (
              <div className='d-flex align-items-start'>
                <i className='bi bi-lightbulb rushmind-onboarding-tip-icon fs-3 me-3 mt-1'></i>
                <div className='flex-grow-1'>
                  <h6 className='rushmind-onboarding-tip-title fw-bold mb-2'>Dica do RushMind</h6>
                  <p className='text-dark fs-4 fw-normal mb-0 lh-base insight-text'>
                    <RushmindInsightText
                      text={insight.insight}
                      className='text-dark'
                      processMarkdown={true}
                    />
                  </p>
                </div>
              </div>
            ) : (
              <p className='text-dark fs-4 fw-normal mb-0 lh-base insight-text'>
                <RushmindInsightText
                  text={insight.insight}
                  className='text-dark'
                  processMarkdown={true}
                />
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderFallbackCard = () => (
    <div className='card card-custom bg-body'>
      <div className='card-body p-3'>
        <div className='mb-0' style={{minHeight: '48px'}}>
          <p className='text-muted fs-6 fw-normal mb-0 lh-base'>
            <i className='bi bi-info-circle text-gray-500 me-2'></i>
            Insight indisponível no momento. Estamos ajustando as sugestões para seu perfil.
          </p>
        </div>
      </div>
    </div>
  )

  const renderUpgradeCtaCard = (isAdmin: boolean) => (
    <div className='card card-custom bg-transparent border-0 shadow-none'>
      <div className='card-body py-4 px-3'>
        <div className='mb-0 d-flex flex-column gap-1'>
          <p className='text-dark fs-6 fw-semibold mb-0 lh-base d-flex align-items-center'>
            <span className='rushmind-upgrade-icon me-2'>
              <i className='bi bi-stars text-brand fs-7'></i>
            </span>
            O RushMind está disponível no plano Growth.
          </p>
          <p className='text-gray-800 fs-7 mb-0 ps-8'>
            Faça upgrade para liberar insights avançados de IA para sua instância.
          </p>
          {isAdmin ? (
            <div className='ps-8 pt-3'>
              <Link to='/billings' className='btn btn-sm btn-primary dash-toolbar-btn-primary'>
                Fazer upgrade
              </Link>
            </div>
          ) : (
            <p className='text-muted fs-7 mb-0 ps-8 pt-3'>
              Solicite ao administrador da sua empresa para realizar o upgrade do plano.
            </p>
          )}
        </div>
      </div>
    </div>
  )

export {AIInsightsGrid}
